export function getWebSocketBaseUrl() {
  const configured = process.env.REACT_APP_WS_URL ||
    (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace(/\/api\/?$/, '');
  let url = configured.replace(/^http:/, 'ws:').replace(/^https:/, 'wss:').replace(/\/$/, '');
  if (url.includes('run.app')) url = url.replace(/^ws:/, 'wss:');
  return url;
}

export class WebSocketService {
  constructor() {
    this.socket = null;
    this.listeners = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.token = null;
    this.reconnectTimer = null;
    this.generation = 0;
  }

  connect(token) {
    if (!token) {
      this.disconnect();
      return;
    }
    if (token === this.token && this.socket &&
        [WebSocket.OPEN, WebSocket.CONNECTING].includes(this.socket.readyState)) {
      return;
    }
    if (token !== this.token) this.disconnect();
    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    this.token = token;
    const generation = ++this.generation;
    const previous = this.socket;
    this.socket = null;
    previous?.close();

    const wsUrl = getWebSocketBaseUrl();
    
    // Check if /ws is already in the URL
    const hasWsPath = wsUrl.includes('/ws');
    
    // Construct final URL: if /ws is not present, add it, then append token
    const finalUrl = hasWsPath 
      ? `${wsUrl}/${token}` 
      : `${wsUrl}/ws/${token}`;
    
    // Connect using the token in the URL path
    const socket = new WebSocket(finalUrl);
    this.socket = socket;
    const isCurrent = () => this.socket === socket && this.token === token && this.generation === generation;

    socket.onopen = () => {
      if (!isCurrent()) return;
      console.log('WebSocket connected');
      this.reconnectAttempts = 0;
    };

    socket.onclose = (event) => {
      if (!isCurrent()) return;
      this.socket = null;
      console.log('WebSocket disconnected');
      // A policy rejection requires a fresh login, not more attempts with the
      // same revoked/expired token. Other closures can be transient outages.
      if (event.code === 1008) {
        this.token = null;
        return;
      }
      // Attempt to reconnect
      if (this.reconnectAttempts < this.maxReconnectAttempts) {
        this.reconnectAttempts++;
        this.reconnectTimer = setTimeout(() => {
          this.reconnectTimer = null;
          if (this.token === token && this.generation === generation) this.connect(token);
        }, 3000);
      }
    };

    socket.onerror = () => {
      if (isCurrent()) console.error('WebSocket connection failed');
    };

    // Handle incoming messages
    socket.onmessage = (event) => {
      if (!isCurrent()) return;
      try {
        const data = JSON.parse(event.data);
        this.handleNotification(data);
      } catch (error) {
        console.error('Failed to parse WebSocket message');
      }
    };
  }

  disconnect() {
    ++this.generation;
    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    this.token = null;
    const previous = this.socket;
    this.socket = null;
    this.reconnectAttempts = 0;
    previous?.close();
  }

  handleNotification(data) {
    const { notification_type, data: payload } = data;
    
    // Notify all listeners for this notification type
    const listeners = this.listeners.get(notification_type) || [];
    listeners.forEach(callback => callback(payload));
    
    // Also notify generic notification listeners
    const genericListeners = this.listeners.get('notification') || [];
    genericListeners.forEach(callback => callback(data));
  }

  subscribe(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
    
    // Return unsubscribe function
    return () => {
      const listeners = this.listeners.get(event) || [];
      const index = listeners.indexOf(callback);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    };
  }

  // Convenience methods for specific notification types
  onEventUpdate(callback) {
    return this.subscribe('event_update', callback);
  }

  onNoteProcessed(callback) {
    return this.subscribe('note_processed', callback);
  }

  onAIResponse(callback) {
    return this.subscribe('ai_response', callback);
  }

  onNewRecommendation(callback) {
    return this.subscribe('new_recommendation', callback);
  }

  onEventEnhanced(callback) {
    return this.subscribe('event_enhanced', callback);
  }

  // Send message to server
  send(message) {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(typeof message === 'string' ? message : JSON.stringify(message));
    }
  }
}

// Create singleton instance
const wsService = new WebSocketService();

export default wsService;
